import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PasswordGate({ onUnlock }) {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password.trim() || loading) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/access/verify`, {
                password,
            });
            if (data.success) {
                localStorage.setItem("heir_access_token", data.token);
                onUnlock();
            }
        } catch (err) {
            setError("Access denied. Please check your credentials.");
            setPassword("");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative min-h-screen w-full flex items-center justify-center bg-navy-950 px-6 overflow-hidden"
            data-testid="password-gate"
        >
            {/* Ambient background glow */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.06] blur-[120px]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#0E0C09_78%)]" />
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="relative z-10 w-full max-w-md"
            >
                {/* Logo mark */}
                <div className="mb-14 flex flex-col items-center text-center">
                    <div className="mb-8 flex items-center justify-center">
                        <img
                            src="/heir-mark-gold.svg"
                            alt="HEIR"
                            className="h-16 w-16 object-contain"
                        />
                    </div>
                    <MaskedLines
                        lines={["HEIR Investor Access"]}
                        className="font-display text-3xl md:text-4xl font-light text-white"
                        delay={0.2}
                    />
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-5 font-mono text-xs uppercase tracking-[0.25em] text-white/50"
                    >
                        Qualified Investors Only · Seed Round
                    </motion.p>
                </div>

                <motion.form
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                >
                    <div className="relative">
                        <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                        <input
                            type="password"
                            data-testid="password-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter access password"
                            autoFocus
                            className="w-full border border-white/15 bg-white/[0.03] py-4 pl-12 pr-4 text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-gold/70 focus:ring-0"
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="password-submit"
                        disabled={loading}
                        className="group flex w-full items-center justify-center gap-3 border border-gold/60 bg-gold/[0.04] py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy-950 disabled:opacity-60"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                Enter
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </>
                        )}
                    </button>

                    <AnimatePresence>
                        {error && (
                            <motion.p
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                data-testid="password-error"
                                className="text-center font-mono text-xs tracking-wide text-red-400"
                            >
                                {error}
                            </motion.p>
                        )}
                    </AnimatePresence>
                </motion.form>

                <p className="mt-14 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/25">
                    Confidential · HEIR PTE. LTD.
                </p>
            </motion.div>
        </div>
    );
}
